import { layer, getSVG } from "@gramex/chartbase";

/**
 * Creates a radar visualization.
 *
 * @param {string|HTMLElement} el - The selector or HTML element for the SVG.
 * @param {Object} params - Parameters for the visualization.
 * @param {Array} params.data - list of row objects (dataframe)
 * @param {number} [params.width] - width of the SVG.
 * @param {number} [params.height] - height of the SVG.
 * @param {number} [params.cx = width/2] - center x-coordinate of the radar.
 * @param {number} [params.cy = height/2] - center y-coordinate of the radar.
 * @param {string|Function} [params.level = "level"] - attribute name or function(row) to get the level
 * @param {string|Function} [params.spoke = "spoke"] - attribute name or function(row) to get the spoke
 * @param {Array|Function} [params.levels] - optional list of levels (in order)
 * @param {Array|Function} [params.spokes] - optional list of spokes (in order)
 * @param {Function} [params.levelScale] - (level, index) => [min, max] angle (degrees) of level
 * @param {Function} [params.spokeScale] - (spoke, index) => [min, max] radius (px) of spoke
 * @param {number} [params.spokeLabelROffset = -10] - offset of spoke labels from the outer radius
 * @param {string} [params.nodeTag = "circle"] - SVG tag for the nodes
 * @param {Object} [params.d3 = window.d3] - D3 instance to use.
 * @returns {Graph} Object containing D3.js selections for cells, nodes, levelLabels, spokeLabels.
 */
export function radar(
  el,
  {
    data,
    width,
    height,
    cx,
    cy,
    level = "level",
    spoke = "spoke",
    levels,
    spokes,
    levelScale,
    spokeScale,
    spokeLabelROffset = -10,
    nodeTag = "circle",
    d3 = globalThis.d3,
  },
) {
  ({ el, width, height } = getSVG(el._groups ? el.node() : el, width, height));

  // Move all data processing to a separate function for unit testing
  let cellData, levelData, spokeData, r;
  ({ cx, cy, level, spoke, levels, spokes, levelScale, spokeScale, cellData, levelData, spokeData, r } = calculate({
    data,
    width,
    height,
    cx,
    cy,
    level,
    spoke,
    levels,
    spokes,
    levelScale,
    spokeScale,
    d3,
  }));

  const svg = d3.select(el);
  const newLayer = (cls) => layer(svg, "g", cls).attr("transform", `translate(${cx},${cy})`);
  const cells = layer(newLayer("cells"), "path", "cell", cellData).attr("d", ({ r0, r1, a0, a1 }) => {
    return `M${p(r0, a0)} L${p(r1, a0)} A${r1},${r1} 0 0,1 ${p(r1, a1)} L${p(r0, a1)} A${r0},${r0} 0 0,0 ${p(r0, a0)}`;
  });
  const levelLabels = layer(newLayer("level-labels"), "text", "level-label", levelData)
    .text(({ level }) => level)
    .attr("text-anchor", "middle")
    .attr("dominant-baseline", "ideographic")
    .attr("transform", ({ r0, r1 }) => `translate(${(r0 + r1) / 2},0)`);
  const spokeRadius = levelData.at(-1).r1 + (spokeLabelROffset ?? 0);
  const spokeLabels = layer(newLayer("spoke-labels"), "text", "spoke-label", spokeData)
    .text(({ spoke }) => spoke)
    .attr("text-anchor", "middle")
    .attr("dominant-baseline", "middle")
    .attr("transform", ({ a0, a1 }) => {
      const a = (a0 + a1) / 2;
      const deg = (a * 180) / Math.PI;
      return `translate(${spokeRadius * Math.cos(a)}, ${spokeRadius * Math.sin(a)}) rotate(${
        0 <= deg && deg <= 180 ? deg - 90 : deg + 90
      })`;
    });
  const nodes = layer(newLayer("nodes"), nodeTag, "node", data).attr("transform", (d) => `translate(${d.x},${d.y})`);

  /**
   * Define the returned graph
   * @typedef {Object} Graph
   * @property {Object} cells - D3.js selection for cells.
   * @property {Object} nodes - D3.js selection for nodes.
   * @property {Object} levelLabels - D3.js selection for level labels.
   * @property {Object} spokeLabels - D3.js selection for spoke labels.
   */
  return { cells, levelLabels, spokeLabels, nodes, r };
}

// Returns the list of spokes or levels from the data or specified list.
// E.g. if data = [{level: 2}, {level: 2}, {level: 1}]
function getList(list, data, accessor, name) {
  // If levels or spokes are specified as a list, use them as is
  // E.g. getList([1, 2, 3], data, d => d.level, "levels") returns [1, 2, 3]
  if (Array.isArray(list)) return list;
  // If levels or spokes are not specified, return unique values (in order) from data
  // E.g. getList(undefined, data, d => d.level, "levels") returns [2, 1]
  else if (!list) return Array.from(new Set(data.map(accessor)));
  // If levels or spokes are specified as a function, return function(frequency of each level/spoke)
  // E.g. getList(counter => [...counter.keys()].sort(), data, d => d.level, "levels") returns [1, 2]
  else if (typeof list === "function") {
    const counter = new Map();
    data.forEach((d) => {
      const key = accessor(d);
      counter.set(key, (counter.get(key) || 0) + 1);
    });
    return list(counter);
  } else throw new Error(`${name} must be an array or function, not ${list}`);
}

function calculate({ data, width, height, cx, cy, level, spoke, levels, spokes, levelScale, spokeScale, d3 }) {
  [cx, cy] = [cx ?? width / 2, cy ?? height / 2];
  [level, spoke] = [functor(level), functor(spoke)];
  [levels, spokes] = [getList(levels, data, level, "levels"), getList(spokes, data, spoke, "spokes")];

  // If spokeScale or levelScale is not specified, distribute them evenly
  let R = Math.min(width, height) / 2;
  if (!levelScale) levelScale = (_, index) => [(index * R) / levels.length, ((index + 1) * R) / levels.length];
  if (!spokeScale) spokeScale = (spoke, index) => [(index * 360) / spokes.length, ((index + 1) * 360) / spokes.length];

  const levelData = levels.map((level, levelIndex) => {
    const [r0, r1] = levelScale(level, levelIndex);
    return { level, levelIndex, r0, r1 };
  });
  R = d3.max(levelData, (d) => d.r1);
  const spokeData = spokes.map((spoke, spokeIndex) => {
    const [a0, a1] = spokeScale(spoke, spokeIndex).map((a) => (a * Math.PI) / 180);
    return { spoke, spokeIndex, a0, a1 };
  });
  // cellData is every combination of spokes and levels
  const cellData = levelData.flatMap((level) => spokeData.map((spoke) => Object.assign({}, level, spoke)));
  // cellMap.get(level).get(spoke) is the cellData for that level and spoke
  const cellMap = d3.index(
    cellData,
    (d) => d.level,
    (d) => d.spoke,
  );
  // Group data by level and spoke using d3
  const dataGroup = d3
    .flatRollup(data, (d) => ({ n: d.length, data: d }), level, spoke)
    .filter(([level, spoke]) => cellMap.has(level) && cellMap.get(level).has(spoke))
    .map(([level, spoke, info]) => Object.assign(cellMap.get(level).get(spoke), info))
    .sort((a, b) => b.n - a.n);

  // Pick a grid size. Count the number of grid points in each cell (starting with cells having the most nodes per area). If it's less than the number of nodes, tighten the grid.
  let gridCount = 10,
    fits = false;
  for (let iteration = 0; iteration < 10 && !fits; iteration++) {
    cellData.forEach((d) => delete d.points);
    d3.cross(d3.range(-gridCount, gridCount), d3.range(-gridCount, gridCount), (i, j) => ({ i, j })).map(({ i, j }) => {
      const x = ((i + 0.5) * R) / gridCount;
      const y = ((j + 0.5) * R) / gridCount;
      const r = Math.sqrt(x * x + y * y);
      let a = Math.atan2(y, x);
      if (a < 0) a += 2 * Math.PI;
      const cell = cellData.find(({ r0, r1, a0, a1 }) => r0 < r && r < r1 && a0 < a && a < a1);
      if (cell) (cell.points = cell.points || []).push({ i, j, x, y, r: r, a });
      return { i, j, x, y, r, a, cell };
    });
    fits = dataGroup.every((d) => d.points?.length >= d.n);
    const scaleFactor = d3.min(dataGroup, (d) => d.points?.length / d.n);
    gridCount += Math.max(2, Math.ceil(gridCount / Math.sqrt(scaleFactor)) - gridCount);
  }
  if (!fits) throw new Error("Cannot fit data. Check levelScale, spokeScale, missing values.");

  // Assign x, y to data points
  dataGroup
    .filter((d) => d.n)
    .forEach((d) => {
      const points = uniformSample([d.points], d.n);
      d.data.forEach((row, index) => Object.assign(row, { x: points[index].x, y: points[index].y }));
    });

  // Calculate the maximum radius of each node
  const r = R / gridCount / 2;

  return { cx, cy, level, spoke, levels, spokes, levelScale, spokeScale, cellData, levelData, spokeData, r };
}

// Get evenly distributed items from a uniformly distributed list by picking the middle,
// then the middle of the left and right, and so on.
// E.g. uniformSample([[1, 2, 3, 4, 5]], 3) returns [3, 2, 4]
function uniformSample(arrays, limit, nextArrayIndex = 0) {
  while (nextArrayIndex < limit && nextArrayIndex < arrays.length) {
    const array = arrays[nextArrayIndex];
    const middle = Math.floor(array.length / 2);
    arrays.splice(nextArrayIndex, 1, array[middle]);
    if (middle > 0) arrays.push(array.slice(0, middle));
    if (middle < array.length - 1) arrays.push(array.slice(middle + 1));
    nextArrayIndex++;
  }
  return arrays;
}

const p = (r, a) => [r * Math.cos(a), r * Math.sin(a)].join();
const functor = (value) => (typeof value === "function" ? value : (d) => d[value]);
